import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, ShoppingCart } from 'lucide-react';
import { Order } from '../../types';
import apiService, { StockWarning } from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

const STATUS_BADGE: Record<string, string> = {
  pendiente: 'bg-red-100 text-red-800 border-red-200',
  confirmado: 'bg-green-100 text-green-700 border-green-200',
  en_produccion: 'bg-gray-100 text-gray-700 border-gray-200',
  completado: 'bg-green-100 text-green-700 border-green-200',
  cancelado: 'bg-blue-100 text-blue-700 border-blue-200'
};

const STATUS_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_produccion: 'En producción',
  completado: 'Completado',
  cancelado: 'Cancelado'
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${STATUS_BADGE[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

const SkeletonRows: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="animate-pulse p-4 space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-12 bg-gray-100 rounded-lg" />
      ))}
    </div>
  </div>
);

const EmptyState: React.FC<{ withFilters: boolean }> = ({ withFilters }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
    <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-800 font-medium">
      {withFilters ? 'No hay resultados para los filtros aplicados' : 'No hay pedidos cargados'}
    </p>
    <p className="text-sm text-gray-500 mt-1">
      {withFilters ? 'Probá cambiar estado o búsqueda.' : 'Cuando ingresen pedidos, aparecerán aquí.'}
    </p>
  </div>
);

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  useBodyScrollLock(!!selectedOrder);
  useEscapeToClose(!!selectedOrder, () => setSelectedOrder(null));
  const [stockWarnings, setStockWarnings] = useState<StockWarning[]>([]);
  const [generatingId, setGeneratingId] = useState<number | null>(null);

  const fetchOrders = async (manualRetry = false) => {
    if (manualRetry) setIsRetrying(true);
    try {
      setLoadError(null);
      const response = await apiService.getOrders({ limit: 200 });
      setOrders(response.orders || []);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setLoadError(detail || 'No se pudieron cargar los pedidos.');
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (selectedStatus !== 'all' && o.status !== selectedStatus) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        o.orderNumber.toLowerCase().includes(q) ||
        o.customerName.toLowerCase().includes(q) ||
        o.customerEmail.toLowerCase().includes(q)
      );
    });
  }, [orders, selectedStatus, searchTerm]);

  const pendingCount = useMemo(() => orders.filter((o) => o.status === 'pendiente').length, [orders]);

  const updateStatus = async (id: number, status: string) => {
    try {
      setInlineError(null);
      await apiService.updateOrderStatus(id, status);
      window.dispatchEvent(new Event('admin:orders-updated'));
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: status as Order['status'] } : o)));
      setSelectedOrder((prev) => (prev && prev.id === id ? { ...prev, status: status as Order['status'] } : prev));
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo actualizar el estado.');
    }
  };

  const generateReceipt = async (order: Order) => {
    if (!window.confirm(`Generar el remito de ${order.orderNumber}? El stock se actualizará al confirmar.`)) return;
    try {
      setGeneratingId(order.id); setInlineError(null); setStockWarnings([]);
      const response = await apiService.generateOrderReceipt(order.id);
      window.dispatchEvent(new Event('admin:orders-updated'));
      setOrders(current => current.map(item => item.id === order.id ? { ...item, status: 'completado', receiptId: response.receiptId } : item));
      setSelectedOrder(current => current?.id === order.id ? { ...current, status: 'completado', receiptId: response.receiptId } : current);
      setStockWarnings(response.stockWarnings || []);
    } catch (error: any) { setInlineError(error?.response?.data?.detail || 'No se pudo generar el remito.'); }
    finally { setGeneratingId(null); }
  };
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Pedidos</h1>
          <p className="text-gray-600">Cargando información...</p>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  const hasFilters = selectedStatus !== 'all' || searchTerm.trim().length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Pedidos</h1>
        <p className="text-gray-600">{orders.length} registros en total · {pendingCount} pendientes</p>
      </div>

      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <button
            onClick={() => fetchOrders(true)}
            disabled={isRetrying}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-red-300 hover:bg-red-100 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            Reintentar
          </button>
        </div>
      )}

      {inlineError && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{inlineError}</div>}

      {stockWarnings.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          <p className="font-medium">El remito se generó, pero no hay stock propio suficiente cargado para:</p>
          <ul className="mt-1 list-disc pl-5">
            {stockWarnings.map((w, i) => (
              <li key={i}>{w.productName}: faltan {w.shortfall.toLocaleString('es-AR')} bolsas (disponibles {w.available.toLocaleString('es-AR')}, se entregaron {w.requested.toLocaleString('es-AR')}).</li>
            ))}
          </ul>
          <p className="mt-1">Revisá si falta cargar una entrada de proveedor en Stock propio.</p>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar pedidos..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="all">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="confirmado">Confirmado</option>
          <option value="en_produccion">En producción</option>
          <option value="completado">Completado</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      {filteredOrders.length === 0 ? (
        <EmptyState withFilters={hasFilters} />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="divide-y md:hidden">
          {filteredOrders.map((o) => (
            <div key={o.id} className={`p-4 space-y-2 ${o.status === 'pendiente' ? 'border-l-2 border-l-red-400' : ''}`}>
              <button onClick={() => setSelectedOrder(o)} className="block w-full text-left space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{o.orderNumber}</span>
                  <StatusBadge status={o.status} />
                </div>
                <p className="text-sm text-gray-700">{o.customerName}</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-500">{formatDateTime(o.createdAt)}</span>
                  <span className="font-semibold">{o.totalAmountARS ? `$${o.totalAmountARS.toLocaleString('es-AR')}` : '-'}</span>
                </div>
              </button>
              <select
                value={o.status}
                onChange={(e) => updateStatus(o.id, e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="pendiente">Pendiente</option>
                <option value="confirmado">Confirmado</option>
                <option value="en_produccion">En producción</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Pedido</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cliente</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Total ARS</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fecha</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => setSelectedOrder(o)}
                  className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${o.status === 'pendiente' ? 'border-l-2 border-l-red-400' : ''}`}
                >
                  <td className="px-4 py-3">{o.orderNumber}</td>
                  <td className="px-4 py-3">{o.customerName}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3">{o.totalAmountARS ? `$${o.totalAmountARS.toLocaleString('es-AR')}` : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(o.createdAt)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="confirmado">Confirmado</option>
                      <option value="en_produccion">En producción</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {selectedOrder && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Detalle del pedido</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Número</p>
                  <p className="font-medium text-gray-900">{selectedOrder.orderNumber}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estado</p>
                  <div className="mt-1"><StatusBadge status={selectedOrder.status} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cliente</p>
                  <p className="font-medium text-gray-900">{selectedOrder.customerName}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                  <a href={`mailto:${selectedOrder.customerEmail}`} className="font-medium text-primary-700 hover:underline">{selectedOrder.customerEmail}</a>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teléfono</p>
                  <p className="font-medium text-gray-900">{selectedOrder.customerPhone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha</p>
                  <p className="font-medium text-gray-900">{formatDateTime(selectedOrder.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Empresa</p>
                  <p className="font-medium text-gray-900">{selectedOrder.customerCompany || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total ARS</p>
                  <p className="font-medium text-gray-900">{selectedOrder.totalAmountARS ? `$${selectedOrder.totalAmountARS.toLocaleString('es-AR')}` : '-'}</p>
                </div>
              <div className="mt-5 overflow-x-auto rounded-lg border"><table className="min-w-full text-sm"><thead className="bg-gray-50 text-gray-500"><tr><th className="px-3 py-2 text-left">Medida</th><th className="px-3 py-2 text-left">Color</th><th className="px-3 py-2 text-right">Packs</th><th className="px-3 py-2 text-right">Bolsas</th></tr></thead><tbody className="divide-y">{selectedOrder.products.map((item:any,index)=><tr key={index}><td className="px-3 py-2">{item.widthCm ? `${item.widthCm} x ${item.lengthCm} cm` : item.product?.name || `Producto ${item.productId}`}</td><td className="px-3 py-2">{item.productColor || '-'}</td><td className="px-3 py-2 text-right">{item.packs || '-'}</td><td className="px-3 py-2 text-right">{Number(item.quantity || 0).toLocaleString('es-AR')}</td></tr>)}</tbody></table></div>
              {selectedOrder.notes && <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-600"><span className="font-medium">Observaciones:</span> {selectedOrder.notes}</div>}              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              {selectedOrder.status === 'pendiente' && <><button onClick={() => updateStatus(selectedOrder.id, 'cancelado')} className="px-4 py-2 rounded-lg border border-red-300 text-red-700">Rechazar</button><button onClick={() => updateStatus(selectedOrder.id, 'confirmado')} className="px-4 py-2 rounded-lg bg-primary-600 text-white">Aceptar pedido</button></>}{!selectedOrder.receiptId && (selectedOrder.status === 'confirmado' || selectedOrder.status === 'en_produccion' || selectedOrder.status === 'completado') && <button onClick={() => generateReceipt(selectedOrder)} disabled={generatingId === selectedOrder.id} className="px-4 py-2 rounded-lg bg-green-600 text-white disabled:opacity-50">{generatingId === selectedOrder.id ? 'Generando...' : 'Generar remito'}</button>}{selectedOrder.receiptId && <span className="mr-auto rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">Remito generado</span>}<button onClick={() => setSelectedOrder(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
